import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'

import { CompanyStaffsContext } from 'contexts/CompanyStaffsContext'
import { ContactContext } from 'contexts/contactContext'
import { AbilityContext } from 'contexts/abilityContext'
import { COMPANY_STAFF_DESTROY_ALL } from 'graphql/CompanyStaffs'

import { Button, Collapse, Modal, Popconfirm, message } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'

import { ExternalStaffs } from './ExternalStaffs'
import { ManagementStaffs } from './ManagementStaffs'
import { SharesStaffs } from './SharesStaffs'
import { StaffBoxCardEdit } from './Cards/StaffBoxCardEdit'

import { StaffBox } from './styles'

// Staff roles displayed in each collapse
const PANEL_ROLES = {
  management: ['leader', 'director', 'comanager'],
  shareholders: ['shareholder'],
  extern: ['host', 'mandataire'],
}

const StaffCollapses = () => {
  const { t } = useTranslation('companyStaffs')
  const { Panel } = Collapse
  const { staffs, refetch } = useContext(CompanyStaffsContext)
  const { contact } = useContext(ContactContext)
  const ability: any = useContext(AbilityContext)
  const [modalOpen, setModalOpen] = useState(false)
  const [destroyAllMutation, { loading: destroying }] = useMutation(
    COMPANY_STAFF_DESTROY_ALL
  )
  const canManage = ability.can('manage', 'CompanyStaff')

  const destroyStaffs = (roles?: string[]) => {
    destroyAllMutation({ variables: { contactId: contact.id, roles } })
      .then(() => refetch())
      .catch(error => message.error(error.message))
  }

  const destroyConfirm = (
    button: React.ReactNode,
    panel?: keyof typeof PANEL_ROLES
  ) =>
    canManage && (
      // stops clicks (popup included, through the portal) from toggling the panel
      <span onClick={event => event.stopPropagation()}>
        <Popconfirm
          title={t('confirm.destroyAll.title')}
          description={t('confirm.destroyAll.description', {
            roles: panel
              ? `« ${t(`types.${panel}`)} »`
              : t('confirm.destroyAll.allRoles'),
          })}
          okText={t('confirm.yes')}
          cancelText={t('confirm.no')}
          okButtonProps={{ danger: true }}
          onConfirm={() => destroyStaffs(panel && PANEL_ROLES[panel])}
        >
          {button}
        </Popconfirm>
      </span>
    )

  const destroyButton = (panel: keyof typeof PANEL_ROLES) =>
    destroyConfirm(
      <Button
        size="small"
        danger
        icon={<DeleteOutlined />}
        loading={destroying}
      >
        {t('submit.destroy')}
      </Button>,
      panel
    )

  const destroyAllButton = destroyConfirm(
    <Button danger icon={<DeleteOutlined />} loading={destroying}>
      {t('submit.destroyAll')}
    </Button>
  )

  if ((staffs || []).length === 0)
    return (
      <>
        <StaffBox>{t('noItem')}</StaffBox>
        {destroyAllButton}
      </>
    )

  return (
    <>
      <Collapse defaultActiveKey={['management']}>
        <Panel
          header={t('types.management')}
          key="management"
          extra={destroyButton('management')}
        >
          <ManagementStaffs />
        </Panel>
        <Panel
          header={t('types.shareholders')}
          key="shareholders"
          extra={destroyButton('shareholders')}
        >
          <SharesStaffs />
        </Panel>
        <Panel
          header={t('types.extern')}
          key="extern"
          extra={destroyButton('extern')}
        >
          <ExternalStaffs />
        </Panel>
      </Collapse>
      {/*CREATION MODAL*/}
      {canManage && (
        <>
          {destroyAllButton}
          <Button onClick={() => setModalOpen(true)}>
            {t('submit.create')}
          </Button>
          <Modal
            title={t(`modal.title`)}
            centered
            open={modalOpen}
            okButtonProps={{ style: { display: 'none' } }}
            onCancel={() => setModalOpen(false)}
          >
            <StaffBoxCardEdit />
          </Modal>
        </>
      )}
      {/*CREATION MODAL*/}
    </>
  )
}

export default StaffCollapses
